import { useCallback, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { message, notification } from 'antd'
import dayjs from 'dayjs'
import { RootState } from '../../../app/store'
import { post } from '../utils/request'
import { PERSONAL_COS_BUCKET, PERSONAL_COS_REGION } from '../utils/cosConfig'
import cos from '../../../utils/cos'
import {
  setPhotos,
  appendPhotos,
  setPage,
  setLoading,
  setFilter,
  resetFilter,
  setWatermarks,
  toggleSelectPhoto,
  toggleSelectAll,
  clearSelection,
  setPersonalVip,
  setPreviewIndex,
  setSearchMode,
  setLastPageCond,
  setSpaceOpen,
  removePhotos,
  PersonalSpaceFilter
} from '../../../slices/mySpace/personalSpaceSlice'
import { useCurrentUser } from './useCurrentUser'
import {
  ENABLE_MOCK,
  mockFetchVipStatus,
  mockFetchWatermarks,
  mockFetchPhotos,
  mockDeletePhotos,
  mockDownloadPhotos
} from './personalSpaceMock'

/** 服务端 photo 对象 → 前端 PersonalPhoto */
function mapPhoto(p: any, index: number, offset = 0) {
  return {
    id: String(p.id),
    seq: offset + index + 1,
    smallURL: p.smallurl || p.middleurl || p.url || '',
    largeURL: p.largeurl || p.url || '',
    videoURL: p.mediaType === 1 ? p.videoURL || p.url : undefined,
    mediaType: (p.mediaType === 1 ? 1 : 0) as 0 | 1,
    // photoTime 为秒级时间戳；time 为字符串 "2026-04-01 10:00:00"
    time: p.photoTime ? p.photoTime * 1000 : new Date(p.time).getTime(),
    addr: p.addr || '',
    watermarkName: p.watermarkName,
    watermarkID: p.watermarkID,
    photoNumber: p.photoNumber,
    sourceType: p.sourceType ?? 0
  }
}

export function usePersonalSpace() {
  const dispatch = useDispatch()
  const state = useSelector((s: RootState) => (s as any).mySpace.personalSpace)
  const { userId, nickname } = useCurrentUser()

  // 用 ref 镜像最新 state，避免 fetchPhotos / searchPhotos 等 useCallback
  // 因为依赖了 state.page 等字段而每次 render 重建（导致下游 useEffect 抖动）。
  // 回调内部一律读 ref，依赖列表只保留 dispatch。
  const filterRef = useRef(state.filter)
  const lastPageCondRef = useRef(state.lastPageCond)
  const photosRef = useRef(state.photos)
  const pageRef = useRef(state.page)
  const pageSizeRef = useRef(state.pageSize)
  const isSearchModeRef = useRef(state.isSearchMode)
  useEffect(() => {
    filterRef.current = state.filter
    lastPageCondRef.current = state.lastPageCond
    photosRef.current = state.photos
    pageRef.current = state.page
    pageSizeRef.current = state.pageSize
    isSearchModeRef.current = state.isSearchMode
  }, [
    state.filter,
    state.lastPageCond,
    state.photos,
    state.page,
    state.pageSize,
    state.isSearchMode
  ])

  // ── 1. 会员状态 ──────────────────────────────────────────────
  const fetchVipStatus = useCallback(async () => {
    try {
      if (ENABLE_MOCK) {
        const data = await mockFetchVipStatus()
        dispatch(setPersonalVip({ isVip: data.isVip, loaded: true }))
        return
      }
      if (!userId) return
      const res = await post<{
        status: number
        personalVip: { isVip: boolean }
      }>('/web/user/vip/info', { userID: userId })
      dispatch(
        setPersonalVip({
          isVip: res.personalVip?.isVip ?? false,
          loaded: true
        })
      )
    } catch {
      dispatch(setPersonalVip({ isVip: false, loaded: true }))
    }
  }, [dispatch, userId])

  // ── 2. 个人空间信息（isOpen / photoCount）────────────────────
  const fetchSpaceInfo = useCallback(async () => {
    try {
      const res = await post<{
        status: number
        isOpen: boolean
        photoCount: number
      }>('/web/user/space/info', {})
      dispatch(setSpaceOpen(res.isOpen === true))
      if (!res.isOpen) {
        console.log('[个人空间] 用户未开通个人空间，isOpen=false')
      }
    } catch {
      // 不阻塞主流程
    }
  }, [dispatch])

  // ── 3. 全量照片（分页，无筛选条件）──────────────────────────
  const fetchPhotos = useCallback(async () => {
    dispatch(setLoading(true))
    const page = pageRef.current
    const pageSize = pageSizeRef.current
    const filter = filterRef.current as PersonalSpaceFilter
    try {
      if (ENABLE_MOCK) {
        const params = {
          page,
          pageSize,
          keyword: filter.keyword || undefined,
          startTime: filter.startTime,
          endTime: filter.endTime,
          watermarkIds:
            filter.watermarkIds.length > 0
              ? filter.watermarkIds.join(',')
              : undefined
        }
        const data = await mockFetchPhotos(params as any)
        dispatch(setPhotos({ list: data.list || [], total: data.total || 0 }))
        return
      }
      const res = await post<{
        status: number
        number: number
        photos: any[]
      }>('/web/user/photo', { page, pageSize })
      if (res.status !== 0) {
        message.error('加载照片失败')
        return
      }
      const list = (res.photos || []).map((p, i) =>
        mapPhoto(p, i, (page - 1) * pageSize)
      )
      dispatch(setPhotos({ list, total: res.number || 0 }))
    } catch {
      message.error('加载照片失败')
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch])

  // ── 4. 搜索照片（游标分页）───────────────────────────────────
  // 返回搜索结果数量（首次搜索时），供调用方上报埋点；加载更多 / 失败时返回 undefined
  const searchPhotos = useCallback(
    async (
      filter: { keyword?: string; startTime?: number; endTime?: number },
      append = false
    ): Promise<number | undefined> => {
      if (!append) {
        dispatch(setLoading(true))
      }
      try {
        const keywords = filter.keyword
          ? filter.keyword
              .trim()
              .split(/[\s,，]+/)
              .filter(Boolean)
          : []
        const timeFilter =
          filter.startTime && filter.endTime
            ? {
                start: dayjs(filter.startTime).format('YYYY-MM-DD HH:mm:ss'),
                end: dayjs(filter.endTime).format('YYYY-MM-DD HH:mm:ss')
              }
            : undefined

        const res = await post<{
          status: number
          number: number
          lastPageCond: string
          photos: any[]
        }>('/web/user/photo/search', {
          lastPageCond: append ? lastPageCondRef.current : '',
          filter: {
            ...(keywords.length > 0 ? { keywords } : {}),
            ...(timeFilter ? { time: timeFilter } : {})
          }
        })
        if (res.status !== 0) {
          message.error('搜索失败')
          return undefined
        }
        const list = (res.photos || []).map((p, i) =>
          mapPhoto(p, i, append ? photosRef.current.length : 0)
        )
        const nextCond = res.lastPageCond || ''
        if (append) {
          dispatch(
            appendPhotos({
              list,
              total: res.number || 0,
              lastPageCond: nextCond
            })
          )
        } else {
          dispatch(setPhotos({ list, total: res.number || 0 }))
          dispatch(setLastPageCond(nextCond))
          dispatch(setSearchMode(true))
        }
        return append ? undefined : res.number || 0
      } catch {
        message.error('搜索失败')
        return undefined
      } finally {
        if (!append) {
          dispatch(setLoading(false))
        }
      }
    },
    [dispatch]
  )

  // ── 5. 水印列表 ───────────────────────────────────────────────
  const fetchWatermarks = useCallback(async () => {
    try {
      if (ENABLE_MOCK) {
        const data = await mockFetchWatermarks()
        dispatch(setWatermarks(data.list || []))
        return
      }
      // 暂无真实接口，留空
    } catch {
      // 不阻塞主流程
    }
  }, [dispatch])

  // ── 6. 删除 ───────────────────────────────────────────────────
  const deletePhotos = useCallback(
    async (ids: string[]) => {
      try {
        if (ENABLE_MOCK) {
          await mockDeletePhotos(ids)
        } else {
          const res = await post<{
            status: number
            payload?: { deletedCount?: number }
          }>('/web/user/photo/delete', { photoIDs: ids })
          if (res.status === -207) {
            message.error('删除失败，一次最多可删除999张')
            return
          }
          if (res.status !== 0) {
            message.error(`删除失败 (status: ${res.status})`)
            return
          }
        }
        message.success(`${ids.length}张照片已删除`)
        dispatch(clearSelection())

        // 重新请求服务端数据，再从结果中剔除已删除的照片（防止搜索索引延迟）
        const deletedSet = new Set(ids)
        if (isSearchModeRef.current) {
          const f = filterRef.current as PersonalSpaceFilter
          const keywords = f.keyword
            ? f.keyword
                .trim()
                .split(/[\s,，]+/)
                .filter(Boolean)
            : []
          const timeFilter =
            f.startTime && f.endTime
              ? {
                  start: dayjs(f.startTime).format('YYYY-MM-DD HH:mm:ss'),
                  end: dayjs(f.endTime).format('YYYY-MM-DD HH:mm:ss')
                }
              : undefined
          const res = await post<{
            status: number
            number: number
            lastPageCond: string
            photos: any[]
          }>('/web/user/photo/search', {
            lastPageCond: '',
            filter: {
              ...(keywords.length > 0 ? { keywords } : {}),
              ...(timeFilter ? { time: timeFilter } : {})
            }
          })
          if (res.status === 0) {
            const rawList = (res.photos || []).map((p: any, idx: number) =>
              mapPhoto(p, idx, 0)
            )
            const filtered = rawList.filter(
              (p: { id: string }) => !deletedSet.has(p.id)
            )
            // ES 索引有延迟，res.number 可能还包含已删除的照片，
            // 用实际被 filter 掉的数量修正总数
            const stillInResult = rawList.length - filtered.length
            dispatch(
              setPhotos({
                list: filtered,
                total: Math.max(0, (res.number || 0) - stillInResult)
              })
            )
            dispatch(setLastPageCond(res.lastPageCond || ''))
          } else {
            dispatch(removePhotos(ids))
          }
        } else {
          const page = pageRef.current
          const pageSize = pageSizeRef.current
          const res = await post<{
            status: number
            number: number
            photos: any[]
          }>('/web/user/photo', { page, pageSize })
          if (res.status === 0) {
            const rawList = (res.photos || []).map((p: any, i: number) =>
              mapPhoto(p, i, (page - 1) * pageSize)
            )
            const filtered = rawList.filter(
              (p: { id: string }) => !deletedSet.has(p.id)
            )
            const stillInResult = rawList.length - filtered.length
            dispatch(
              setPhotos({
                list: filtered,
                total: Math.max(0, (res.number || 0) - stillInResult)
              })
            )
          } else {
            dispatch(removePhotos(ids))
          }
        }
      } catch (err) {
        console.error('[个人空间] 删除照片失败:', err)
        message.error('删除失败，请检查网络后重试')
      }
    },
    [dispatch]
  )

  // ── 7. 轮询下载任务进度（内部工具函数）──────────────────────
  // 间隔 5s，最多 120 次（约 10 分钟），相比团队的"持续 setInterval"更保守，
  // 但比之前 5 分钟硬超时更宽松
  const pollDownloadTask = useCallback(
    async (taskID: string): Promise<string | null> => {
      const MAX_POLL = 120
      const INTERVAL = 5000
      for (let i = 0; i < MAX_POLL; i++) {
        await new Promise((r) => setTimeout(r, INTERVAL))
        try {
          const res = await post<{
            status: number
            items: Array<{
              taskID: string
              zips: Array<{ url: string; status: number; progress: number }>
            }>
          }>('/web/user/photo/downloads', {})
          const task = (res.items || []).find((t) => t.taskID === taskID)
          if (task && task.zips && task.zips.length > 0) {
            const zip = task.zips[0]
            // status: 1=处理中 2=完成 3=失败（与团队端 src/actions/photo.js 约定一致）
            if (zip.status === 3) return null
            if (zip.status === 2 && zip.url) return zip.url
          }
        } catch {
          // 轮询失败继续重试
        }
      }
      return null // 超时
    },
    []
  )

  // ── 8. 批量下载 ───────────────────────────────────────────────
  // 不再使用 DownloadModal 收集配置（服务端目前只接受 photoIDs）。
  // 用 notification 而非 message，每个任务一条独立通知，避免并发互相覆盖。
  const downloadPhotos = useCallback(
    async (
      ids: string[],
      config?: {
        folderRule: 'none' | 'date' | 'address' | 'watermark'
        namingFields: string[]
      }
    ) => {
      if (ENABLE_MOCK) {
        await mockDownloadPhotos()
        message.success('下载任务已创建')
        return
      }
      // 后端约定：folderName 同时作为 zip 文件名 + 内层根文件夹名
      // 格式：{昵称}的个人空间照片 {YYYY-MM-DD}
      const dateStr = dayjs().format('YYYY-MM-DD')
      const folderName = `${nickname || '我'}的个人空间照片 ${dateStr}`

      const payload: Record<string, any> = { photoIDs: ids, folderName }
      if (config) {
        if (config.folderRule && config.folderRule !== 'none') {
          payload.folderRule = config.folderRule
        }
        const ALLOWED_NAMING = [
          'time',
          'user',
          'address',
          'watermark',
          'photoNumber'
        ]
        const fields = (config.namingFields || []).filter((f) =>
          ALLOWED_NAMING.includes(f)
        )
        if (fields.length > 0) payload.namingFields = fields
      }

      let res: { status: number; payload: string }
      try {
        res = await post<{ status: number; payload: string }>(
          '/web/user/photo/export',
          payload
        )
      } catch (err) {
        message.error(typeof err === 'string' ? err : '创建下载任务失败')
        return
      }
      // 业务错误码处理（参考 src/actions/photo.js exportPhotos）
      // -33：服务端按用户粒度拒绝并发导出。不能复用上次打包结果，
      // 因为那份 zip 可能与本次勾选的照片无关，会给用户错误数据。
      // 只提示用户等待老任务完成或重试。
      if (res.status === -33) {
        message.warning('已下载过此组照片，无需重复下载')
        return
      }
      if (!res.payload) {
        message.error('创建下载任务失败')
        return
      }
      const notifyKey = `download-${res.payload}`
      notification.open({
        key: notifyKey,
        message: '下载任务已创建',
        description: `${ids.length} 张照片正在打包，完成后会自动开始下载...`,
        duration: 0
      })
      const url = await pollDownloadTask(res.payload)
      notification.destroy(notifyKey)
      if (url) {
        // 用 location.href 触发下载，避免 window.open 在异步回调里被弹窗拦截
        window.location.href = url
        notification.success({
          message: '打包完成',
          description: '已开始下载，可在浏览器下载列表中查看'
        })
      } else {
        notification.error({
          message: '打包失败',
          description: '任务超时或失败，请稍后重试'
        })
      }
    },
    [pollDownloadTask, nickname]
  )

  // ── 9. 上传照片/视频 ─────────────────────────────────────────
  const uploadPhoto = useCallback(
    async (file: File): Promise<boolean> => {
      try {
        // 1. 读取媒体信息（图片尺寸 / 视频时长）
        const isVideo = file.type.startsWith('video/')
        const mediaType: 0 | 1 = isVideo ? 1 : 0
        const meta = await new Promise<{
          width: number
          height: number
          duration: number
        }>((resolve) => {
          if (isVideo) {
            const v = document.createElement('video')
            const objUrl = URL.createObjectURL(file)
            const finish = (data: {
              width: number
              height: number
              duration: number
            }) => {
              URL.revokeObjectURL(objUrl)
              resolve(data)
            }
            v.preload = 'metadata'
            v.onloadedmetadata = () =>
              finish({
                width: v.videoWidth,
                height: v.videoHeight,
                duration: Math.round(v.duration)
              })
            v.onerror = () => finish({ width: 0, height: 0, duration: 0 })
            v.src = objUrl
          } else {
            const img = new Image()
            const objUrl = URL.createObjectURL(file)
            const finish = (data: {
              width: number
              height: number
              duration: number
            }) => {
              URL.revokeObjectURL(objUrl)
              resolve(data)
            }
            img.onload = () =>
              finish({
                width: img.naturalWidth,
                height: img.naturalHeight,
                duration: 0
              })
            img.onerror = () => finish({ width: 0, height: 0, duration: 0 })
            img.src = objUrl
          }
        })

        // 2. 上传到腾讯云 COS
        const ext = file.name.includes('.')
          ? file.name.slice(file.name.lastIndexOf('.'))
          : ''
        const key = `personal-space/${
          userId || 'anon'
        }/${Date.now()}-${Math.random().toString(36).slice(2, 8)}${ext}`
        await new Promise<void>((resolve, reject) => {
          cos.uploadFile(
            {
              Bucket: PERSONAL_COS_BUCKET,
              Region: PERSONAL_COS_REGION,
              Key: key,
              Body: file,
              SliceSize: 1024 * 1024 * 30
            },
            (err: unknown) => (err ? reject(err) : resolve())
          )
        })

        // 3. 调用上传接口
        const now = dayjs().format('YYYY-MM-DD HH:mm:ss')
        const body = {
          checkTime: now,
          time: now,
          location: '',
          lat: 0,
          lng: 0,
          file_name: key,
          location_type: 1,
          isHD: 1,
          mediaType,
          inChina: true,
          imageInfo: {
            fileSize: isVideo ? 0 : file.size,
            width: meta.width,
            height: meta.height
          },
          videoInfo: {
            duration: meta.duration,
            width: isVideo ? meta.width : 0,
            height: isVideo ? meta.height : 0,
            fileSize: isVideo ? file.size : 0
          },
          userComment: {
            ver: '1',
            data: {
              fileName: file.name,
              userID: userId || '',
              groupWatermarkID: '',
              watermarkBaseID: '10',
              watermarkID: '10',
              watermarkContentExtension: {},
              baseInfo: {
                locationDetail: {
                  country: '',
                  province: '',
                  city: '',
                  district: '',
                  township: '',
                  poi: ''
                },
                location: '',
                locationPoi: '',
                locationType: '1',
                latitude: '',
                time: now,
                timeType: '0',
                timeZone: '8',
                screenType: '0',
                longitude: '',
                frontRearCam: 'rear',
                altitude: '',
                imageDirection: '',
                ua: {
                  os: 'Web',
                  osVersion: '',
                  manufacturer: '',
                  model: '',
                  deviceID: '',
                  appVersion: '1.0.0',
                  appName: 'work.xhey.top'
                },
                weather: {
                  temperature: '',
                  humidity: '',
                  windpower: '',
                  type: '',
                  winddirection: ''
                }
              },
              watermarkContent: [],
              ver: '1',
              mediaType,
              mediaSubtypes: 0,
              livePhotoInfo: {
                displayTime: 0,
                pairedVideoName: '',
                pairedVideoUrl: ''
              },
              ocrContent: ''
            },
            others: ''
          },
          sourceType: 0
        }
        await post<{ status: number; photoID: number }>(
          '/web/user/photo/upload',
          body as any
        )
        return true
      } catch {
        return false
      }
    },
    [userId]
  )

  // ── 10. 单张下载 ──────────────────────────────────────────────
  // 单张下载：直接拿原图 URL，用 blob 方式触发下载。
  // 不能直接 window.location.href = url —— COS 原图无 Content-Disposition 头，
  // 浏览器会在当前 tab 跳转到图片地址（视觉上像"进入全屏看图"），而不会下载。
  // 也不能用 <a download> —— 跨域 URL 上 download 属性会被浏览器忽略。
  // 因此先 fetch 成 blob，再用 blob URL + <a download> 触发真正的下载。
  const downloadSinglePhoto = useCallback(async (id: string) => {
    const photo = photosRef.current.find((p: { id: string }) => p.id === id)
    if (!photo) {
      message.error('照片不存在')
      return
    }
    const url =
      photo.mediaType === 1
        ? photo.videoURL || photo.largeURL
        : photo.largeURL || photo.smallURL
    if (!url) {
      message.error('照片地址为空')
      return
    }
    try {
      const resp = await fetch(url)
      if (!resp.ok) throw new Error('fetch failed')
      const blob = await resp.blob()
      const blobUrl = URL.createObjectURL(blob)
      const urlPath = url.split('?')[0]
      const filename =
        urlPath.slice(urlPath.lastIndexOf('/') + 1) || `photo-${id}`
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
    } catch {
      message.error('下载失败')
    }
  }, [])

  return {
    ...state,
    setPage: (p: number) => dispatch(setPage(p)),
    setFilter: (f: Partial<PersonalSpaceFilter>) => dispatch(setFilter(f)),
    resetFilter: () => dispatch(resetFilter()),
    toggleSelectPhoto: (id: string) => dispatch(toggleSelectPhoto(id)),
    toggleSelectAll: () => dispatch(toggleSelectAll()),
    clearSelection: () => dispatch(clearSelection()),
    setPreviewIndex: (i: number) => dispatch(setPreviewIndex(i)),
    exitSearchMode: () => {
      dispatch(setSearchMode(false))
      dispatch(setLastPageCond(''))
    },
    fetchVipStatus,
    fetchSpaceInfo,
    fetchWatermarks,
    fetchPhotos,
    searchPhotos,
    deletePhotos,
    downloadPhotos,
    downloadSinglePhoto,
    uploadPhoto
  }
}
